import type { IDataTableColumn, IDataTableFacetValue } from '../DataTable.types';
import type {
    IDataTableFacetRequest,
    IDataTableSource,
    IServerLoadOptions,
    IServerLoadResult,
    ServerLoader,
} from '../dataTableServerSource';
import { createDxloExcelExport } from './dxloExcelExport';
import { createDxloServerLoad, DxloFetch } from './dxloServerSource';

/** Значение воронки: `value` уходит в условие `[поле, '=', value]`, ключ в воронке — его строка. */
export interface IDxloFacetOption {
    value: string | number | boolean;
    label: string;
}

/** Значения воронки колонки: известный набор или запрос набора по фильтру выборки (уже серверному — после
 *  `filter` и `mapFilter`). Счётчик значения источник берёт сам — `totalCount` выборки с этим значением. */
export type DxloFacetValues =
    readonly IDxloFacetOption[] | ((filter: unknown, signal: AbortSignal) => Promise<readonly IDxloFacetOption[]>);

/** Выгрузка: файл .xlsx всей выборки по видимым колонкам или своя (серверная) выгрузка. */
export type DxloExport<R> =
    { fileName: string; columns: readonly IDataTableColumn<R>[] } | NonNullable<IDataTableSource<R>['export']>;

export interface IDxloSourceOptions<T, R = T> {
    /** Запрос к эндпоинту с `DataSourceLoadOptionsParser`: параметры выборки (уже с `filter` и `mapFilter`) в строку
     *  запроса переводит приложение своим `getLoadOptionsQuery`. */
    fetch: DxloFetch<T>;
    /** Условие набора поверх фильтров таблицы (окно дат, поиск страницы) — первым через «и». */
    filter?: unknown;
    /** Фильтр таблицы → фильтр эндпоинта (операции, которые он понимает; условие по умолчанию). */
    mapFilter?: (filter: unknown) => unknown;
    /** Строка ответа → строка таблицы; `index` — место строки во всей выборке. */
    mapRow?: (row: T, index: number) => R;
    /** Ключ строки. С ним источник отдаёт ключи «выбрать все» тем же запросом, что и строки (фильтр не теряется),
     *  и таблица по умолчанию выбирает все строки выборки, а не страницы. */
    keyOf?: (row: R) => string;
    /** Воронки колонок: поле → значения. Колонке без значений воронка источника отдаёт только отмеченные. */
    facets?: Readonly<Record<string, DxloFacetValues>>;
    export?: DxloExport<R>;
    /** Поля глобального поиска на сервере. */
    searchFields?: string[];
}

/** Части фильтра через «и»; пустые пропускаются, `undefined` — фильтра нет. */
export function andFilters(...parts: unknown[]): unknown {
    const present = parts.filter((part) => part != null && !(Array.isArray(part) && part.length === 0));
    if (present.length < 2) return present[0];
    return present.flatMap((part, index) => (index ? ['and', part] : [part]));
}

/** Счётчики значений воронки: запрос с `take: 1` на значение — `totalCount` выборки с ним. Отмеченные значения,
 *  которых нет в наборе, остаются в списке со счётчиком 0 — иначе их не снять. */
async function countFacets<R>(
    load: ServerLoader<R>,
    { field, options, selected }: IDataTableFacetRequest,
    values: readonly IDxloFacetOption[],
    signal: AbortSignal,
): Promise<IDataTableFacetValue[]> {
    const counted = await Promise.all(
        values.map(async ({ value, label }) => {
            const { totalCount } = await load(
                { skip: 0, take: 1, requireTotalCount: true, filter: andFilters(options.filter, [field, '=', value]) },
                signal,
            );
            return { key: String(value), label, count: totalCount };
        }),
    );
    const known = new Set<string | null>(counted.map((value) => value.key));
    const missing = selected
        .filter((key) => !known.has(key))
        .map((key) => ({ key, label: key ?? '(Пусто)', count: 0 }));
    return [...counted, ...missing];
}

/**
 * Источник DataTable (`source`) над эндпоинтом, разбирающим loadOptions DevExtreme
 * (`DataSourceLoadOptionsParser`): строки, ключи «выбрать все», воронки и выгрузка — одним и тем же запросом
 * с фильтром таблицы. Проводной формат — у приложения (`fetch`, см. `createDxloServerLoad`).
 * Функция без состояния: источник постоянного набора можно создать один раз на модуль; набор с параметрами
 * страницы — `useDxloSource`.
 */
export function dxloSource<T, R = T>(options: IDxloSourceOptions<T, R>): IDataTableSource<R> {
    const { fetch, filter: base, mapFilter, mapRow, keyOf, facets, searchFields, export: exportFile } = options;
    const serverFilter = (filter: unknown) => andFilters(base, mapFilter ? mapFilter(filter) : filter);
    const fetchLoad = createDxloServerLoad<T>(fetch);
    const load: ServerLoader<R> = async (loadOptions, signal) => {
        const result = await fetchLoad({ ...loadOptions, filter: serverFilter(loadOptions.filter) }, signal);
        if (!mapRow) return result as unknown as IServerLoadResult<R>;
        return { ...result, rows: result.rows.map((row, index) => mapRow(row, loadOptions.skip + index)) };
    };
    return {
        load,
        keys:
            keyOf &&
            (async (loadOptions: IServerLoadOptions, signal: AbortSignal) =>
                (await load(loadOptions, signal)).rows.map(keyOf)),
        facets:
            facets &&
            (async (request, signal) => {
                const values = facets[request.field] ?? [];
                const known =
                    typeof values === 'function' ? await values(serverFilter(request.options.filter), signal) : values;
                return countFacets(load, request, known, signal);
            }),
        export:
            typeof exportFile === 'function'
                ? exportFile
                : exportFile && createDxloExcelExport(load, exportFile.fileName, exportFile.columns),
        searchFields,
    };
}
